"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var getInheritableStyles_1 = require("./getInheritableStyles");
var htmlToDom_1 = require("./htmlToDom");
var isHTMLElement_1 = require("../typeUtils/isHTMLElement");
var cloneObject_1 = require("./cloneObject");
var getAllowedValues_1 = require("./getAllowedValues");
/**
 * HTML sanitizer class provides two featuers:
 * 1. Convert global CSS to inline CSS
 * 2. Sanitize an HTML document, remove unnecessary/dangerous attribute/nodes
 */
var HtmlSanitizer = /** @class */ (function () {
    /**
     * Construct a new instance of HtmlSanitizer
     * @param options Options for HtmlSanitizer
     */
    function HtmlSanitizer(options) {
        options = options || {};
        this.elementCallbacks = cloneObject_1.cloneObject(options.elementCallbacks);
        this.styleCallbacks = getAllowedValues_1.getStyleCallbacks(options.styleCallbacks);
        this.attributeCallbacks = cloneObject_1.cloneObject(options.attributeCallbacks);
        this.allowedTags = getAllowedValues_1.getAllowedTags(options.additionalAllowedTags);
        this.allowedAttributes = getAllowedValues_1.getAllowedAttributes(options.additionalAllowAttributes);
        this.allowedCssClassesRegex = getAllowedValues_1.getAllowedCssClassesRegex(options.additionalAllowedCssClasses);
        this.defaultStyleValues = getAllowedValues_1.getDefaultStyleValues(options.additionalDefaultStyleValues);
        this.additionalGlobalStyleNodes = options.additionalGlobalStyleNodes || [];
        this.allowPreserveWhiteSpace = options.allowPreserveWhiteSpace;
    }
    /**
     * Convert global CSS to inline CSS if any
     * @param html HTML source
     * @param additionalStyleNodes (Optional) additional HTML STYLE elements used as global CSS
     */
    HtmlSanitizer.convertInlineCss = function (html, additionalStyleNodes) {
        var sanitizer = new HtmlSanitizer({
            additionalGlobalStyleNodes: additionalStyleNodes,
        });
        return sanitizer.exec(html, true /*convertCssOnly*/);
    };
    /**
     * Sanitize HTML string, remove any unuseful HTML node/attribute/CSS.
     * @param html HTML source string
     * @param options Options used for this sanitizing process
     */
    HtmlSanitizer.sanitizeHtml = function (html, options) {
        options = options || {};
        var sanitizer = new HtmlSanitizer(options);
        var currentStyles = isHTMLElement_1.default(options.currentElementOrStyle)
            ? getInheritableStyles_1.default(options.currentElementOrStyle)
            : options.currentElementOrStyle;
        return sanitizer.exec(html, options.convertCssOnly, options.preserveFragmentOnly, currentStyles);
    };
    /**
     * Sanitize HTML string
     * This function will do the following work:
     * 1. Convert global CSS into inline CSS
     * 2. Remove dangerous HTML tags and attributes
     * 3. Remove useless CSS properties
     * @param html The input HTML
     * @param convertInlineCssOnly Whether only convert inline css and skip html content sanitizing
     * @param preserveFragmentOnly If set to true, only preserve the html content between &lt;!--StartFragment--&gt; and &lt;!--Endfragment--&gt;
     * @param currentStyles Current inheritable CSS styles
     */
    HtmlSanitizer.prototype.exec = function (html, convertCssOnly, preserveFragmentOnly, currentStyles) {
        var doc = htmlToDom_1.default(html, preserveFragmentOnly);
        if (doc) {
            this.convertGlobalCssToInlineCss(doc);
            if (!convertCssOnly) {
                this.sanitize(doc.body, currentStyles);
            }
        }
        return (doc && doc.body && doc.body.innerHTML) || '';
    };
    /**
     * Sanitize an HTML element, remove unnecessary or dangerous elements/attribute/CSS rules
     * @param rootNode Root node to sanitize
     * @param currentStyles Current CSS styles. Inheritable styles in the given node which has
     * the same value with current styles will be ignored.
     */
    HtmlSanitizer.prototype.sanitize = function (rootNode, currentStyles) {
        if (!rootNode) {
            return '';
        }
        currentStyles = cloneObject_1.cloneObject(currentStyles, getInheritableStyles_1.default(null));
        this.processNode(rootNode, currentStyles, {});
    };
    /**
     * Convert global CSS into inline CSS
     * @param rootNode The HTML Document
     */
    HtmlSanitizer.prototype.convertGlobalCssToInlineCss = function (rootNode) {
        var styleNodes = toArray(rootNode.querySelectorAll('style'));
        var styleSheets = this.additionalGlobalStyleNodes
            .reverse()
            .map(function (node) { return node.sheet; })
            .concat(styleNodes.map(function (node) { return node.sheet; }).reverse())
            .filter(function (sheet) { return sheet; });
        for (var _i = 0, styleSheets_1 = styleSheets; _i < styleSheets_1.length; _i++) {
            var styleSheet = styleSheets_1[_i];
            var _loop_1 = function (j) {
                // Skip any none-style rule, i.e. @page
                var styleRule = styleSheet.cssRules[j];
                var text = styleRule && styleRule.style ? styleRule.style.cssText : null;
                if (styleRule.type != CSSRule.STYLE_RULE || !text || !styleRule.selectorText) {
                    return "continue";
                }
                // Make sure the selector is not empty
                for (var _i = 0, _a = styleRule.selectorText.split(','); _i < _a.length; _i++) {
                    var selector = _a[_i];
                    if (!selector || !selector.trim() || selector.indexOf(':') >= 0) {
                        continue;
                    }
                    var nodes = toArray(rootNode.querySelectorAll(selector));
                    // Always put existing styles after so that they have higher priority
                    // Which means if both global style and inline style apply to the same element,
                    // inline style will have higher priority
                    nodes.forEach(function (node) {
                        return node.setAttribute('style', text + (node.getAttribute('style') || ''));
                    });
                }
            };
            for (var j = styleSheet.cssRules.length - 1; j >= 0; j--) {
                _loop_1(j);
            }
        }
        styleNodes.forEach(function (node) {
            if (node.parentNode) {
                node.parentNode.removeChild(node);
            }
        });
    };
    HtmlSanitizer.prototype.processNode = function (node, currentStyle, context) {
        var nodeType = node.nodeType;
        var isElement = nodeType == 1 /* Element */;
        var isText = nodeType == 3 /* Text */;
        var isFragment = nodeType == 11 /* DocumentFragment */;
        var element = node;
        var tag = isElement ? element.tagName.toUpperCase() : '';
        if ((isElement && !this.allowElement(element, tag, context)) ||
            (isText && /^[\r\n]*$/g.test(node.nodeValue) && !currentStyle.insidePRE) ||
            (!isElement && !isText && !isFragment)) {
            node.parentNode.removeChild(node);
        }
        else if (isText &&
            !this.allowPreserveWhiteSpace &&
            currentStyle['white-space'] == 'pre') {
            node.nodeValue = node.nodeValue.replace(/^ /gm, '\u00A0').replace(/ {2}/g, ' \u00A0');
        }
        else if (isElement || isFragment) {
            var thisStyle = cloneObject_1.cloneObject(currentStyle);
            if (isElement) {
                this.processAttributes(element, context);
                this.processCss(element, tag, thisStyle, context);
                // Special handling for PRE tag, need to preserve \r\n inside PRE
                if (tag == 'PRE') {
                    thisStyle.insidePRE = 'true';
                }
            }
            var child = element.firstChild;
            var next = void 0;
            for (; child; child = next) {
                next = child.nextSibling;
                this.processNode(child, thisStyle, context);
            }
        }
    };
    HtmlSanitizer.prototype.processCss = function (element, tag, thisStyle, context) {
        var _this = this;
        var styleNode = element.getAttributeNode('style');
        if (!styleNode) {
            return;
        }
        var source = styleNode.value.split(';');
        var result = source.filter(function (style) {
            var pair;
            if (!style || style.trim() == '' || (pair = style.split(':')).length != 2) {
                return false;
            }
            var name = pair[0].trim().toLowerCase();
            var value = pair[1].trim().toLowerCase();
            var callback = _this.styleCallbacks[name];
            var isInheritable = thisStyle[name] != undefined;
            var keep = (!callback || callback(value, element, context)) &&
                value != 'inherit' &&
                value.indexOf('expression') < 0 &&
                name.substr(0, 1) != '-' &&
                _this.defaultStyleValues[name] != value &&
                ((isInheritable && value != thisStyle[name]) ||
                    (!isInheritable && value != 'initial' && value != 'normal'));
            if (keep && isInheritable) {
                thisStyle[name] = value;
            }
            return keep && (_this.allowPreserveWhiteSpace || name != 'white-space');
        });
        if (source.length != result.length) {
            if (result.length > 0) {
                element.setAttribute('style', result.map(function (s) { return s.trim(); }).join('; '));
            }
            else {
                element.removeAttribute('style');
            }
        }
    };
    HtmlSanitizer.prototype.processAttributes = function (element, context) {
        for (var i = element.attributes.length - 1; i >= 0; i--) {
            var attribute = element.attributes[i];
            var name_1 = attribute.name.toLowerCase().trim();
            var value = attribute.value;
            var callback = this.attributeCallbacks[name_1];
            var newValue = callback
                ? callback(value, element, context)
                : this.allowedAttributes.indexOf(name_1) >= 0 || name_1.indexOf('data-') == 0
                    ? value
                    : null;
            if (name_1 == 'class' && this.allowedCssClassesRegex) {
                newValue = this.processCssClass(value, newValue);
            }
            if (newValue === null ||
                newValue === undefined ||
                newValue.match(/s\n*c\n*r\n*i\n*p\n*t\n*:/i) // match script: with any NewLine inside. Browser will ignore those NewLine char and still treat it as script prefix
            ) {
                element.removeAttribute(name_1);
            }
            else {
                attribute.value = newValue;
            }
        }
    };
    HtmlSanitizer.prototype.processCssClass = function (originalValue, calculatedValue) {
        var _this = this;
        var originalClasses = originalValue ? originalValue.split(' ') : [];
        var calculatedClasses = calculatedValue ? calculatedValue.split(' ') : [];
        originalClasses.forEach(function (className) {
            if (_this.allowedCssClassesRegex.test(className) &&
                calculatedClasses.indexOf(className) < 0) {
                calculatedClasses.push(className);
            }
        });
        return calculatedClasses.length > 0 ? calculatedClasses.join(' ') : null;
    };
    HtmlSanitizer.prototype.allowElement = function (element, tag, context) {
        var callback = this.elementCallbacks[tag];
        return callback
            ? callback(element, context)
            : this.allowedTags.indexOf(tag) >= 0 || tag.indexOf(':') > 0;
    };
    return HtmlSanitizer;
}());
exports.default = HtmlSanitizer;
function toArray(list) {
    return [].slice.call(list);
}
//# sourceMappingURL=HtmlSanitizer.js.map